/**
 * Shared presenter for the secondary "meta" line under a person's name in
 * pickers and assignment lists (ADO 398).
 *
 * Renders location and role as a single middot-separated line:
 *   "Phoenix North Clinic · BCBA"
 *   "No location · BCBA"        (members without a primary center still show role)
 *   "Phoenix North Clinic"      (no role → no trailing separator)
 *
 * The separator lives here rather than in a translation string so every picker
 * renders it identically.
 */

export const PERSON_META_SEPARATOR = ' · ';

export interface PersonMetaParts {
  centerName?: string | null;
  roleName?: string | null;
  /** Shown when centerName is null — callers pass their own translated "No location". */
  locationFallback?: string;
}

/**
 * Build the meta line. Returns an empty string only when there is nothing at all
 * to show (no location, no fallback, no role) — callers can treat that as "hide".
 */
export const formatPersonMeta = ({
  centerName,
  roleName,
  locationFallback,
}: PersonMetaParts): string => {
  const location = centerName ?? locationFallback ?? null;
  return [location, roleName].filter(Boolean).join(PERSON_META_SEPARATOR);
};

export interface SearchablePerson {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  centerName?: string | null;
  roleName?: string | null;
  roleNames?: string[] | null;
}

/**
 * Shared picker predicate (ADO 406) — matches a person against a free-text query
 * on name, email, location/center and role. Case-insensitive; an empty query
 * matches everyone so callers can pass it straight through to `.filter()`.
 *
 * Matches the full name as one string so "ann lee" works, not just "ann".
 */
export const matchesPerson = (person: SearchablePerson, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const haystack = [
    `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim(),
    person.email ?? '',
    person.centerName ?? '',
    person.roleName ?? '',
    ...(person.roleNames ?? []),
  ];

  return haystack.some((field) => field.toLowerCase().includes(q));
};

export interface RankableAssignableUser {
  isCaseloadOwner: boolean;
  canOwn: boolean;
}

/**
 * Shared ordering for an owner/assignee picker's candidate list (ADO 452 /
 * ADO 535): the current caseload owner first (surfaced immediately), then
 * other eligible candidates, then ineligible ones last. Used by both
 * RedDotDetailPage's inline reassign field and AssigneePickerModal's queue
 * assign flow so the two pickers can't drift apart.
 */
export const sortAssignableUsers = <T extends RankableAssignableUser>(users: T[]): T[] => {
  const rank = (u: T) => (u.isCaseloadOwner ? 0 : u.canOwn ? 1 : 2);
  return [...users].sort((a, b) => rank(a) - rank(b));
};
